import type { MapInfoWindow } from '@angular/google-maps';
import { Icon } from '../shared/icons/models/icon.interface';
import { MatSymbol } from '../shared/icons/models/mat-symbol.interface';

/**
 * A map marker as it arrives over the wire. `TInfo` is whatever the feature puts in
 * `infoWindowData` for its own info-window template to read — untyped (`any`) until C3, and
 * `unknown` by default now, so a consumer that cares has to say what it sends.
 *
 * This is a **data** model: it deliberately carries no Angular view constructs. It used to
 * declare `infoWindowTemplate?: TemplateRef<any>`, which meant an HTTP-deserialized type held a
 * `TemplateRef` that only two components could ever set, and `GoogleMapsService` existed purely
 * to stamp one template onto every marker in a payload. The template is now a single
 * `pozi-google-maps` input.
 */
export interface Marker<TInfo = unknown> {
  position: google.maps.LatLngLiteral;
  matsymbol?: MatSymbol;
  icon?: Icon;
  infoWindowData?: TInfo;
  label?: string;
}

/**
 * Context a marker info-window template is stamped with — `let-marker` in the consumer's
 * `<ng-template>`. Typing the `TemplateRef` with it is what keeps `GoogleMapsComponent`'s
 * `infoWindowTemplate` input off `TemplateRef<any>`.
 */
export interface MarkerInfoWindowContext<TInfo = unknown> {
  $implicit: Marker<TInfo>;
}

/**
 * Context a polygon info-window template is stamped with. Both keys are *named* — there is no
 * `$implicit` here, so a consumer writes `let-zone="polygon"` (as `session-map` does).
 *
 * `polygonInfoWindow` hands the consumer `pozi-google-maps`' own `MapInfoWindow` wrapper so its
 * template can close the bubble from a header button. That leaks a component-internal view object
 * across the input boundary, which is worth knowing about rather than hiding behind
 * `TemplateRef<any>`; the type is written down so a future replacement of that channel is a
 * compile error at every call site instead of a silent `any`.
 */
export interface PolygonInfoWindowContext {
  polygon: Polygon;
  polygonInfoWindow: MapInfoWindow;
}

export interface Polyline {
  positions: google.maps.LatLngLiteral[];
  strokeColor?: string;
  strokeWeight?: number;
}

export interface Polygon {
  id: number;
  name: string;
  path: google.maps.LatLngLiteral[];
  strokeColor?: string;
  strokeWeight?: number;
  fillColor?: string;
  fillOpacity?: number;
  zIndex?: number;
}

export interface Svg {
  svg: string;
  bounds: Bounds;
  layer?: string;
  metadata?: { [key: string]: any };
}

export interface Bounds {
  min: google.maps.LatLngLiteral;
  max: google.maps.LatLngLiteral;
}

// map option settings for all google maps
export const baseMapOptions: google.maps.MapOptions = {
  mapId: '76ea02d65be105ff', // generated via Google Cloud Platform https://console.cloud.google.com/, a custom map style is assigned to the id
  clickableIcons: false,
  disableDefaultUI: true,
  tilt: 0,
  restriction: {
    latLngBounds: {
      north: 85,
      south: -85,
      west: -180,
      east: 180,
    },
    strictBounds: true,
  },
  minZoom: 3,
};

/*
 * Both overlays `extends google.maps.OverlayView`, which only exists once the SDK has loaded —
 * hence a factory rather than a top-level class. Neither class body captures anything, so one
 * definition per page is enough and each factory memoizes it: before, every appended SVG and
 * every map-type switch minted a fresh class. `GoogleMapsLoaderService` guarantees a single
 * SDK instance per page, so the captured base class cannot go stale.
 */

/**
 * The elements a route SVG may carry. Every payload either backend sends is `svg` / `g` / `path`
 * and nothing else — verified across all five `svgs`-bearing mocks and the captured 1.1 MB session
 * route (94 `path`, 2 `g`, 2 `svg`, zero anything else) — so this is an **allow-list**: an element
 * that is not on it is dropped. That fails safe, which a block-list cannot, because the vectors it
 * would have to enumerate keep growing: `<script>`, `<foreignObject>` (arbitrary HTML, including
 * `<img onerror>`), `<use href>`, `<image>`, `<animate>`, `<set>`, `<style>`.
 */
const ALLOWED_SVG_ELEMENTS = new Set(['svg', 'g', 'path']);

/**
 * The presentation attributes those same payloads use, lower-cased for comparison — the HTML parser
 * case-adjusts SVG attributes (`viewbox` → `viewBox`), so neither side can be trusted to match
 * verbatim. Everything else goes, which is what removes `on*` handlers and `href` / `xlink:href`.
 *
 * `style` stays: the root `<svg>` carries its `width`/`height` there, and a style *attribute* cannot
 * execute script in any browser this app supports (`expression()` was IE-only).
 */
const ALLOWED_SVG_ATTRIBUTES = new Set([
  'class',
  'd',
  'fill',
  'preserveaspectratio',
  'stroke',
  'stroke-linecap',
  'stroke-linejoin',
  'style',
  'viewbox',
]);

/**
 * Drops every element and attribute not on the two allow-lists above, in place.
 *
 * `querySelectorAll` returns a static list, so removing while iterating it is safe: a subtree
 * removed early is still visited, and both `remove()` and `removeAttributeNode()` are no-ops on a
 * node that is already detached.
 */
const scrubSvgTree = (root: Element): void => {
  root.querySelectorAll('*').forEach((element: Element) => {
    if (!ALLOWED_SVG_ELEMENTS.has(element.localName.toLowerCase())) {
      element.remove();
      return;
    }

    for (const attribute of Array.from(element.attributes)) {
      if (!ALLOWED_SVG_ATTRIBUTES.has(attribute.name.toLowerCase())) {
        element.removeAttributeNode(attribute);
      }
    }
  });
};

/**
 * Parses server-supplied SVG markup into a live-document element, dropping anything not on the
 * allow-lists on the way through.
 *
 * This replaced a bare `svgElement.innerHTML = svg`. That assignment does not execute `<script>`
 * (the HTML spec forbids it), but it is still a trust boundary crossed without a check: SVG can
 * carry `onload` handlers, which *do* fire once the node is in the document, and `<foreignObject>`,
 * whose HTML children can carry any handler at all. Angular's sanitizer is not an option here —
 * `SecurityContext.HTML`'s allow-list is the HTML element set, with no `svg`, `g` or `path` in it,
 * so `DomSanitizer.sanitize()` would strip the entire route.
 *
 * Two deliberate choices:
 *
 * - **`DOMParser` gives an inert document** — no browsing context, so nothing renders, no
 *   subresource is fetched and no handler fires. The markup is therefore scrubbed *before* it can
 *   ever be live, rather than cleaned up afterwards.
 * - **`text/html`, not `image/svg+xml`.** The payload's root `<svg>` declares no `xmlns`, and XML
 *   parsing would leave it in the null namespace, where it does not render at all. HTML parsing
 *   puts it in the SVG namespace via foreign-content handling *and* is the exact algorithm
 *   `innerHTML` used, so the markup this tolerates has not changed.
 *
 * `adoptNode`, not `importNode`: adopting moves the subtree into the live document, where importing
 * would deep-clone it — and on a session route that means copying ~564 KiB of `d` attributes for
 * nothing, since the inert document is discarded either way.
 */
const parseSvgMarkup = (markup: string): HTMLElement => {
  const inert = new DOMParser().parseFromString(
    `<div>${markup}</div>`,
    'text/html'
  );
  const wrapper = inert.body.firstElementChild ?? inert.createElement('div');

  scrubSvgTree(wrapper);

  return document.adoptNode(wrapper) as HTMLElement;
};

const buildSvgOverlayClass = () => {
  return class SvgOverlay extends google.maps.OverlayView {
    private div: HTMLElement | undefined;

    constructor(
      private bounds: google.maps.LatLngBounds,
      private svg: string,
      private mapPanesKey: keyof google.maps.MapPanes = 'overlayLayer'
    ) {
      super();
    }

    /**
     * Idempotent: the DOM is built — and the markup parsed — on the first attach only, so a
     * later `setMap(map)` re-uses it. On a session route that string is ~564 KiB, and every
     * layer or route-speed toggle used to re-parse it from scratch.
     */
    override onAdd(): void {
      this.div ??= this.buildDiv();

      const panes = this.getPanes();
      panes![this.mapPanesKey].appendChild(this.div);
    }

    /**
     * The single parse site. Nesting matters: the SCSS targets `.svg-container > div > svg`, and
     * `parseSvgMarkup` returns that inner `div` with the parsed route inside it.
     */
    private buildDiv(): HTMLElement {
      const div = document.createElement('div');
      div.style.borderStyle = 'none';
      div.style.borderWidth = '0px';
      div.style.position = 'absolute';
      div.className = 'svg-container';

      div.appendChild(parseSvgMarkup(this.svg));

      return div;
    }

    override draw(): void {
      const overlayProjection = this.getProjection();
      const sw = overlayProjection.fromLatLngToDivPixel(
        this.bounds.getSouthWest()
      );
      const ne = overlayProjection.fromLatLngToDivPixel(
        this.bounds.getNorthEast()
      );

      if (this.div) {
        this.div.style.left = sw!.x + 'px';
        this.div.style.top = ne!.y + 'px';
        this.div.style.width = ne!.x - sw!.x + 'px';
        this.div.style.height = sw!.y - ne!.y + 'px';
      }
    }

    /**
     * Detaches only — `div` is deliberately kept, which is what makes a re-attach free. The
     * overlay is dropped with its component, so the parsed DOM is not retained beyond it.
     */
    override onRemove(): void {
      this.div?.remove();
    }
  };
};

const buildMapOverlayClass = () => {
  return class MapOverlay extends google.maps.OverlayView {
    private div?: HTMLDivElement;
    private backgroundColor = '#14181C';
    private roadmapOpacity = '0.08';
    private satelliteOpacity = '0.24';

    /**
     * The pan buffer is sized from the viewport, which cannot change during a drag — so it is
     * read once per attach rather than twice per `draw()`. Reading layout and writing styles in
     * the same call is a forced reflow, and the SDK calls `draw()` repeatedly throughout a pan
     * (~11/s measured on a session map), per map instance.
     */
    private viewportWidth = 0;
    private viewportHeight = 0;
    private readonly onViewportResize = () => this.cacheViewportSize();

    constructor(private map: google.maps.Map) {
      super();
      this.setMap(map);
    }

    override onAdd() {
      this.cacheViewportSize();
      window.addEventListener('resize', this.onViewportResize);

      this.div = document.createElement('div');
      this.div.classList.add('transparent-map-overlay');

      Object.assign(this.div.style, {
        position: 'absolute',
        background: this.backgroundColor,
        opacity: this.currentOpacity(),
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      });

      const panes = this.getPanes();
      panes!.mapPane.appendChild(this.div);
    }

    /**
     * Retints in place. Switching map type used to rebuild the whole overlay for this one
     * value — and because the old one was detached with `onRemove()` rather than
     * `setMap(null)`, it stayed registered on the map and kept drawing forever.
     */
    setMapTypeOpacity(): void {
      if (this.div) {
        this.div.style.opacity = this.currentOpacity();
      }
    }

    private currentOpacity(): string {
      return this.map.getMapTypeId() === 'roadmap'
        ? this.roadmapOpacity
        : this.satelliteOpacity;
    }

    private cacheViewportSize(): void {
      this.viewportWidth = document.documentElement.clientWidth;
      this.viewportHeight = document.documentElement.clientHeight;
    }

    override draw() {
      if (!this.div) return;

      const projection = this.getProjection();
      const bounds =
        projection.getVisibleRegion()?.latLngBounds || this.map.getBounds();

      if (!bounds) return;

      const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest())!;
      const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast())!;

      const zoom = this.map.getZoom();

      // adding extra space to avoid frame upon drag or zoom changes
      const bufferX =
        zoom !== undefined && zoom <= 4
          ? this.viewportWidth
          : this.viewportWidth / 2;
      const bufferY = this.viewportHeight / 2;

      /*
       * Written synchronously. This used to be deferred in a `setTimeout`, which cost a
       * change-detection tick per `draw()` (zone.js patches timers) and landed a frame late with
       * the previous frame's projection coordinates. The buffer above is wide enough that the
       * lag was never visible — measured — so the timer bought nothing.
       */
      Object.assign(this.div.style, {
        left: `${sw.x - bufferX}px`,
        top: `${ne.y - bufferY}px`,
        width: `${ne.x - sw.x + 2 * bufferX}px`,
        height: `${sw.y - ne.y + 2 * bufferY}px`,
      });
    }

    /** Nulls `div`, so `draw()`'s guard keeps holding for an overlay that has been detached. */
    override onRemove() {
      window.removeEventListener('resize', this.onViewportResize);
      this.div?.remove();
      this.div = undefined;
    }
  };
};

type SvgOverlayClass = ReturnType<typeof buildSvgOverlayClass>;
type MapOverlayClass = ReturnType<typeof buildMapOverlayClass>;

/** Instance types of the two lazily-built overlay classes, so callers need no `any`. */
export type SvgOverlay = InstanceType<SvgOverlayClass>;
export type MapOverlay = InstanceType<MapOverlayClass>;

let svgOverlayClass: SvgOverlayClass | undefined;
let mapOverlayClass: MapOverlayClass | undefined;

export const createSvgOverlayClass = (): SvgOverlayClass =>
  (svgOverlayClass ??= buildSvgOverlayClass());

export const createMapOverlayClass = (): MapOverlayClass =>
  (mapOverlayClass ??= buildMapOverlayClass());

/**
 * Drops both memos so the next `create*OverlayClass()` rebuilds from the
 * `google.maps.OverlayView` that is live **now**. For the unit suite only — nothing in the
 * app calls it, and the memoization it undoes is load-bearing in the browser (one class
 * definition per page, see above).
 *
 * The suite needs it because the memos are module-level and the test runner is
 * **non-isolated** (`@angular/build:unit-test` defaults `isolate` to `false`, "to align with
 * the Karma/Jasmine experience"), so every spec in a Vitest worker shares one instance of
 * this module. Four specs install a different `OverlayView` fake, each a different subset of
 * the real base class, and whichever file called a factory first handed its fake's subclass
 * to every later file — which is how `models/map.spec.ts` came to fail nine tests on
 * `this.getPanes is not a function` in ~1 of 5 full runs while passing alone every time.
 * `src/testing/test-setup.ts` calls this before every test, making the class order-independent.
 */
export const resetOverlayClassesForTest = (): void => {
  svgOverlayClass = undefined;
  mapOverlayClass = undefined;
};

export const DEFAULT_POLYGON_STROKE_COLOR = '#fefefe'; // pozi white
export const DEFAULT_POLYGON_STROKE_WEIGHT = 2; // px
export const DEFAULT_POLYGON_FILL_COLOR = '#14181c'; // pozi black
export const DEFAULT_POLYGON_FILL_OPACITY = 0.2;
